import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError, tokenStore } from "../api/client";
import { DataTable, type Column } from "../components/DataTable";
import { Icon } from "../components/Icon";
import "./dashboard.css";

/**
 * DESIGN_SYSTEM.md §7's Reports screen — generated exports, most recent first.
 *
 * This is where an export goes when it isn't downloaded the moment it finishes:
 * a render that failed, or one started before a meeting and wanted afterwards.
 * A failed job is listed with its reason rather than hidden, so "I clicked
 * export and nothing happened" always has an answer on this page.
 */

interface ExportRow {
  exportId: string;
  status: "queued" | "rendering" | "ready" | "failed";
  fileName: string | null;
  byteSize: number | null;
  filters: {
    days?: number;
    pillarId?: string;
    engine?: string;
    persona?: string;
  } | null;
  dataAsOf: string | null;
  error: string | null;
  createdAt: string | null;
  completedAt: string | null;
  downloadUrl: string | null;
}

const STATUS_BADGE: Record<ExportRow["status"], string> = {
  queued: "badge--neutral",
  rendering: "badge--info",
  ready: "badge--success",
  failed: "badge--error",
};

export function ReportsPage() {
  const { activeBrand } = useAuth();
  const brandId = activeBrand?.brandId ?? null;
  const [rows, setRows] = useState<ExportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      const data = await api<{ items: ExportRow[] }>(
        `/brands/${brandId}/exports`,
      );
      setRows(data.items);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Could not load reports.",
      );
      setRows([]);
    }
  }, [brandId]);

  useEffect(() => {
    void load();
  }, [load]);

  // A job still rendering will finish without the user doing anything, so the
  // list refreshes itself rather than making them reload the page.
  useEffect(() => {
    if (!rows?.some((r) => r.status === "queued" || r.status === "rendering"))
      return;
    const t = setTimeout(() => void load(), 3000);
    return () => clearTimeout(t);
  }, [rows, load]);

  async function download(row: ExportRow) {
    if (!row.downloadUrl) return;
    const res = await fetch(row.downloadUrl, {
      headers: {
        Authorization: `Bearer ${tokenStore.access ?? ""}`,
        ...(brandId ? { "x-brand-id": brandId } : {}),
      },
    });
    if (!res.ok) {
      setError("That report could not be downloaded.");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = row.fileName ?? "narrative-report.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const columns: Column<ExportRow>[] = [
    {
      key: "created",
      header: "Generated",
      width: "190px",
      value: (r) => r.createdAt ?? "",
      render: (r) =>
        r.createdAt ? new Date(r.createdAt).toLocaleString() : "—",
    },
    {
      key: "status",
      header: "Status",
      width: "130px",
      value: (r) => r.status,
      render: (r) => (
        <span className={`badge ${STATUS_BADGE[r.status]}`}>{r.status}</span>
      ),
    },
    {
      key: "scope",
      header: "Scope",
      value: (r) =>
        [
          r.filters?.days ? `${r.filters.days} days` : "",
          r.filters?.engine ?? "",
        ]
          .filter(Boolean)
          .join(" "),
      render: (r) => (
        <>
          <div>Last {r.filters?.days ?? 30} days</div>
          <div className="muted">
            {[
              r.filters?.pillarId ? "one pillar" : "all pillars",
              r.filters?.engine ? r.filters.engine : "all engines",
              r.filters?.persona ? r.filters.persona : "all personas",
            ].join(" · ")}
          </div>
        </>
      ),
    },
    {
      key: "dataAsOf",
      header: "Data as of",
      width: "180px",
      value: (r) => r.dataAsOf ?? "",
      render: (r) =>
        r.dataAsOf ? (
          new Date(r.dataAsOf).toLocaleString()
        ) : (
          // Stated, not blank — a report over a period with no scored runs is a
          // real thing to hand a board, as long as it says so.
          <span className="muted">no scored runs</span>
        ),
    },
    {
      key: "size",
      header: "Size",
      width: "90px",
      align: "right",
      value: (r) => r.byteSize ?? 0,
      render: (r) => (
        <span className="numeric">
          {r.byteSize
            ? `${Math.max(1, Math.round(r.byteSize / 1024))} KB`
            : "—"}
        </span>
      ),
    },
    {
      key: "action",
      header: "",
      width: "150px",
      render: (r) =>
        r.status === "ready" ? (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => void download(r)}
          >
            <Icon name="reports" /> Download
          </button>
        ) : r.status === "failed" ? (
          <span className="muted" title={r.error ?? ""}>
            {(r.error ?? "Generation failed").slice(0, 40)}
          </span>
        ) : (
          <span className="muted">Working…</span>
        ),
    },
  ];

  return (
    <div className="stack">
      <header className="page__header">
        <h1 className="page__title">Reports</h1>
        <p className="page__subtitle">
          Board-ready exports of the dashboard. Generate a new one from the
          dashboard so it carries the filters you were looking at.
        </p>
      </header>

      {error ? <div className="alert alert--error">{error}</div> : null}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(r) => r.exportId}
        searchPlaceholder="Search reports…"
        empty={{
          icon: "reports",
          title: "No reports generated yet",
          description:
            "Set the period and filters you want on the dashboard, then use Export PDF — the report reproduces exactly that view.",
          action: (
            <Link className="btn btn--primary" to="/">
              Go to the dashboard
            </Link>
          ),
        }}
      />
    </div>
  );
}
